import React, { useState, useCallback, useMemo } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemText,
    Toolbar,
    Box,
    ListItemButton,
    Button,
    Typography,
    Avatar,
    Divider,
    Collapse,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { getAccessToken, logout } from '../services/authService';

// ---------- COLORS ----------
const COLORS = {
    gradientNavBg: "linear-gradient(135deg, #5c5a5a 10%, #4ab7bd 40%, #2b2b2b 70%)",
    glass: "rgba(255,255,255,0.13)",
    shadow: "0 8px 38px rgba(50,220,199,0.13)",
    brandText: "#fff",
    brandBg: "#24b9d4",
    iconActiveBg: "#9cfeec",
    iconInactiveBg: "#e6faf9",
    iconColor: "#1ab98a",
    iconUnselected: "#199982",
    selected: "#fff",
    unselected: "#e5fff8",
    logoutBtnBg: "#fff",
    logoutBtnColor: "#1ab98a",

    borderTop: "#38f9d7",
    borderRight: "#1ab98a",
    borderBottom: "#43e97b",
    rightRadius: 28,
};
// ---------- END COLORS ----------

export interface MenuItem {
    text: string;
    icon: React.ElementType;
    path?: string;
    children?: MenuItem[];
    color?: string;
}

export interface RichNavDrawerProps {
    menuItems: MenuItem[];
    onLogout: () => void;
    drawerWidth?: number;
}

const CircularIcon: React.FC<{
    icon: React.ElementType;
    selected?: boolean;
    size?: number;
    color?: string;
}> = ({
          icon,
          selected = false,
          size = 32,
          color = "#1ab98a"
      }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            bgcolor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1,
            flexShrink: 0,
            border: `1.5px solid ${selected ? color : `${color}40`}`,
            boxShadow: selected ? `0 0 8px ${color}66` : "none", // Subtle glow when selected
        }}
    >
        {React.createElement(icon, {
            style: {
                color: color,
                fontSize: size === 32 ? 18 : 16,
                opacity: selected ? 1 : 0.85,
            },
        })}
    </Box>
);

const RichNavDrawer: React.FC<RichNavDrawerProps> = ({
                                                         menuItems,
                                                         onLogout,
                                                         drawerWidth = 200,
                                                     }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

    const handleToggle = useCallback((text: string) => {
        setOpenMenus((prev) => ({ ...prev, [text]: !prev[text] }));
    }, []);

    const handleMenuItemClick = (item: any) => {
        if (item.isExternal && item.path === "/queues") {
            const token = getAccessToken();
            if (!token) {
                console.error("No token found. Please log in again.");
                return;
            }
            const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            const url = `${baseUrl}/api/admin/queues?token=${token}`;

            window.open(url, '_blank');
        } else {
            // Standard internal navigation
            navigate(item.path);
        }
    };

    const renderMenuItem = useCallback(
        (item: MenuItem, nested = false) => {
            const isSelected = !!item.path && location.pathname.startsWith(item.path);

            if (item.children && item.children.length > 0) {
                const childSelected = item.children.some(
                    (c) => c.path && location.pathname.startsWith(c.path)
                );
                const isMenuOpen = !!openMenus[item.text];

                return (
                    <React.Fragment key={item.text}>
                        <ListItemButton
                            onClick={() => handleToggle(item.text)}
                            selected={childSelected}
                            aria-expanded={isMenuOpen}
                            aria-controls={`submenu-${item.text}`}
                            sx={{
                                mx: 1,
                                my: 0.2,
                                borderRadius: 3,
                                minHeight: 40,
                                px: 1.5,
                                background: childSelected ? COLORS.glass : "transparent",
                                color: childSelected ? COLORS.selected : COLORS.unselected,
                                "&:hover": { background: COLORS.glass },
                            }}
                        >
                            <CircularIcon
                                icon={item.icon}
                                selected={isSelected}
                                size={28}
                                color={item.color}
                            />
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                            />
                            {isMenuOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>

                        <Collapse
                            in={isMenuOpen}
                            timeout="auto"
                            unmountOnExit
                            sx={{ ml: 4 }}
                            id={`submenu-${item.text}`}
                        >
                            <List disablePadding>
                                {item.children.map((child) => renderMenuItem(child, true))}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            }

            if (!item.path) return null; // safety check

            return (
                <ListItem key={item.text} disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuItemClick(item)}
                        selected={isSelected}
                        sx={{
                            mx: 1,
                            my: 0.2,
                            borderRadius: 3,
                            minHeight: nested ? 34 : 38,
                            px: nested ? 2 : 1.5,
                            background: isSelected ? COLORS.glass : "transparent",
                            color: isSelected ? COLORS.selected : COLORS.unselected,
                            "&:hover": { background: COLORS.glass },
                        }}
                    >
                        <CircularIcon
                            icon={item.icon}
                            selected={isSelected}
                            size={28}
                            color={item.color}
                        />
                        <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{ fontSize: 12 }}
                        />
                    </ListItemButton>
                </ListItem>
            );
        },
        [location.pathname, openMenus, handleToggle, handleMenuItemClick]
    );

    const renderedMenu = useMemo(
        () => menuItems.map((item) => renderMenuItem(item)),
        [menuItems, renderMenuItem]
    );

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                zIndex: 1400,
                [`& .MuiDrawer-paper`]: {
                    width: drawerWidth,
                    boxSizing: "border-box",
                    background: COLORS.gradientNavBg,
                    backdropFilter: "blur(14px)",
                    boxShadow: COLORS.shadow,
                    borderRight: `4px solid ${COLORS.borderRight}`,
                    borderTop: `4px solid ${COLORS.borderTop}`,
                    borderBottom: `4px solid ${COLORS.borderBottom}`,
                    borderTopRightRadius: COLORS.rightRadius,
                    borderBottomRightRadius: COLORS.rightRadius,
                    borderLeft: "none",
                    color: COLORS.brandText,
                    overflow: "hidden",
                    zIndex: 1400,
                },
            }}
        >
            <Toolbar />
            <motion.div
                initial={{ opacity: 0, y: -35 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <Box sx={{ pt: 1, pb: 2, textAlign: "center" }}>
                    <Avatar
                        sx={{
                            bgcolor: COLORS.brandBg,
                            width: 40,
                            height: 40,
                            mx: "auto",
                            boxShadow: COLORS.shadow,
                            mb: 0.5,
                        }}
                    >
                        <DirectionsCarFilledIcon
                            sx={{ fontSize: 22, color: COLORS.brandText }}
                        />
                    </Avatar>
                    <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{ fontSize: 13, letterSpacing: 1 }}
                    >
                        SwiftaGo Admin
                    </Typography>
                </Box>
            </motion.div>

            <Divider sx={{ borderColor: COLORS.glass, mx: 2, mb: 1 }} />

            <List dense>{renderedMenu}</List>

            <Box
                sx={{
                    position: "absolute",
                    bottom: 24,
                    left: 0,
                    width: "100%",
                    textAlign: "center",
                    px: 1.5,
                }}
            >
                <Button
                    variant="contained"
                    startIcon={<LogoutIcon fontSize="small" />}
                    onClick={onLogout}
                    sx={{
                        bgcolor: COLORS.logoutBtnBg,
                        color: COLORS.logoutBtnColor,
                        fontWeight: 700,
                        fontSize: 12,
                        px: 2.5,
                        py: 0.5,
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(50,220,199,0.12)",
                        "&:hover": {
                            bgcolor: COLORS.iconActiveBg,
                            color: COLORS.brandText,
                        },
                    }}
                >
                    Logout
                </Button>
            </Box>
        </Drawer>
    );
};

export default React.memo(RichNavDrawer);
